import React, { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * TablePagination Component
 * Enhanced pagination with page numbers, first/last buttons, and URL integration
 * Uses 1-based indexing to match backend API
 */
function TablePagination({
  paginationData,
  isSmallScreen,
  paginatedDataLength,
  totalRecords,
  onPageChange,
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read from URL params (source of truth) - these update automatically when URL changes
  const currentPage = Number.parseInt(searchParams.get("page")) || 1;
  const pageSize = Number.parseInt(searchParams.get("pageSize")) || 10;

  // Get total from paginationData or fallback
  const total = paginationData?.total ?? totalRecords ?? 0;

  // Calculate total pages - prioritize backend value, otherwise calculate from total/pageSize
  // Backend may return limit instead of pageSize
  const backendPageSize = paginationData?.pageSize || paginationData?.limit || pageSize;
  const totalPages = Math.max(
    1,
    paginationData?.totalPages ?? (total > 0 ? Math.ceil(total / backendPageSize) : 1)
  );

  // Handle page change (1-based)
  const handlePageChange = useCallback(
    (newPage) => {
      // Clamp to valid bounds
      const targetPage = Math.max(1, Math.min(newPage, totalPages));
      if (targetPage === currentPage) return;

      const params = new URLSearchParams(searchParams);
      params.set("page", String(targetPage));
      params.set("pageSize", String(pageSize));
      setSearchParams(params, { replace: true });
      onPageChange?.(targetPage, pageSize);
    },
    [searchParams, setSearchParams, pageSize, totalPages, currentPage, onPageChange]
  );

  // Handle rows per page change
  const handleRowsPerPageChange = useCallback(
    (e) => {
      const newPageSize = Number(e.target.value);

      const params = new URLSearchParams(searchParams);
      params.set("pageSize", String(newPageSize));
      params.set("page", "1"); // Reset to first page when changing page size
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Calculate visible page numbers (show 5 pages around current)
  const getVisiblePages = () => {
    if (totalPages <= 1) return [1];
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [];
    const showPages = 5;
    let start = Math.max(1, currentPage - Math.floor(showPages / 2));
    let end = Math.min(totalPages, start + showPages - 1);

    // Adjust start if we're near the end
    if (end - start < showPages - 1) {
      start = Math.max(1, end - showPages + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const visiblePages = getVisiblePages();
  // Calculate hasPrev/hasNext: prefer backend (paginationData), then derive from current page and totalPages
  const hasPrev = paginationData?.hasPrev ?? currentPage > 1;
  const hasNext =
    paginationData?.hasNext !== undefined
      ? Boolean(paginationData.hasNext)
      : currentPage < totalPages && totalPages > 1;

  if (isSmallScreen) {
    return (
      <div className="ems-pager ems-pager-mobile">
        <span className="ems-pg-info">
          <b>{paginatedDataLength || 0}</b> of <b>{total}</b>
        </span>
        <div className="ems-pg-nav">
          <button
            className="ems-pg-btn"
            disabled={!hasPrev}
            onClick={() => handlePageChange(currentPage - 1)}
            aria-label="Previous page"
          >
            ◀
          </button>
          <span className="ems-pg-page">
            <b>{currentPage}</b> / <b>{totalPages}</b>
          </span>
          <button
            className="ems-pg-btn"
            disabled={!hasNext}
            onClick={() => handlePageChange(currentPage + 1)}
            aria-label="Next page"
          >
            ▶
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ems-pager">
      <span className="ems-pg-info">
        Showing <b>{paginatedDataLength || 0}</b> of <b>{total}</b> records
      </span>
      <div className="ems-pg-spacer" />
      <div className="ems-rpp">
        Rows per page
        <select
          className="ems-toolbar-select"
          value={pageSize}
          onChange={handleRowsPerPageChange}
          style={{ minWidth: 72 }}
        >
          {[10, 25, 50, 100]
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .concat(pageSize)
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .sort((a, b) => a - b)
            .map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
        </select>
      </div>
      <span className="ems-pg-page">
        Page <b>{currentPage}</b> of <b>{totalPages}</b>
      </span>
      <div className="ems-pg-nav">
        <button
          className="ems-pg-btn"
          disabled={!hasPrev || currentPage === 1}
          onClick={() => handlePageChange(1)}
          aria-label="First page"
        >
          ⏮
        </button>
        <button
          className="ems-pg-btn"
          disabled={!hasPrev}
          onClick={() => handlePageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          ◀
        </button>
        {totalPages > 1 &&
          visiblePages.map((pageNum) => (
            <button
              key={pageNum}
              className={"ems-pg-num" + (pageNum === currentPage ? " active" : "")}
              onClick={() => handlePageChange(pageNum)}
            >
              {pageNum}
            </button>
          ))}
        <button
          className="ems-pg-btn"
          disabled={!hasNext}
          onClick={() => handlePageChange(currentPage + 1)}
          aria-label="Next page"
        >
          ▶
        </button>
        <button
          className="ems-pg-btn"
          disabled={!hasNext || currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
          aria-label="Last page"
        >
          ⏭
        </button>
      </div>
    </div>
  );
}

export default TablePagination;
